import {
  CSSProperties,
  ReactNode,
  memo,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { UseFormRegister } from 'react-hook-form';
import Box from '@mui/material/Box';
import styles from './Table.module.scss';
import TableFooter from './TableFooter/ui/TableFooter';
import TableHeader from './TableHeader/ui/TableHeader';
import TableRows from './TableRows/ui/TableRows';
import { Column, SortDirection } from '../model/table';

interface TableProps {
  columns: Column[];
  data: any[];
  rowActions?: (classname: string, data?: any) => ReactNode;
  pagination?: boolean;
  style?: CSSProperties;
  additionalAction?: ReactNode;
  roundLastRow?: boolean;
  rowHeight?: string;
  register?: UseFormRegister<any>;
  tableKey?: string;
  itemsPerPage?: number;
  onRowClick?: (data?: any) => void;
  noDataRenderer?: () => ReactNode;
  selectable?: boolean;
  selectedRows?: string[];
  setSelectedRows?: React.Dispatch<React.SetStateAction<string[]>>;
  initSortColumnKey?: string;
  initSortDirection?: SortDirection;
  initialPage?: number;
  ignoreResetPage?: boolean;
}

/**
 * @param props - The props for the component.
 * @param columns - The columns array.
 * @param data - The table data
 * @param rowActions - Actions which is called if the user click to the row
 * @param pagination - Indicates whether table should have a pagination
 * @param style - The table styles.
 * @param additionalAction - Additional actions for the table.
 * @param roundLastRow - Indicates whether last row should be rounded
 * @param rowHeight - The row height
 * @param tableKey - The key of the table
 * @param itemsPerPage - Indicates items count per page
 * @param onRowClick - Function which is called after clicking on row
 * @param noDataRenderer - UI for empty table
 * @param register - the path name to the form field value, name is required and unique
 * @param initSortColumnKey - The init key for sorting in table
 * @param initSortDirection - The init direction for sorting in table
 * @param initialPage - The initial page number
 * @param ignoreResetPage - Indicates whether the page should be reset when data changes
 * @returns A React component that displays the tab header.
 */

function TableComponent({
  columns,
  data,
  selectable,
  selectedRows,
  setSelectedRows,
  rowActions,
  pagination = false,
  style,
  additionalAction,
  roundLastRow = false,
  rowHeight = '60px',
  register,
  tableKey,
  itemsPerPage = 9,
  onRowClick,
  noDataRenderer,
  initSortColumnKey = 'updated_at',
  initSortDirection = SortDirection.Descending,
  initialPage = 0,
  ignoreResetPage = false,
}: TableProps) {
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [activeSortColumnKey, setActiveSortColumnKey] =
    useState<string>(initSortColumnKey);
  const [sortDirection, setSortDirection] =
    useState<SortDirection>(initSortDirection);
  const [sortedData, setSortedData] = useState(data); // Keep sorted data in state

  useEffect(() => {
    if (ignoreResetPage) {
      return;
    }

    setCurrentPage(0);
  }, [data, ignoreResetPage]);

  // Handle sorting whenever sort parameters change
  useEffect(() => {
    const sortColumn = columns.find((col) => col.key === activeSortColumnKey);
    const sortMethod = sortColumn?.sortMethod;
    if (sortMethod) {
      const newData: any = sortMethod(
        [...data],
        sortDirection,
        activeSortColumnKey
      );
      setSortedData(newData);
    } else {
      setSortedData(data); // Reset to original data if no sort column is selected
    }
  }, [activeSortColumnKey, sortDirection, data, columns]);

  useEffect(() => {
    setCurrentPage(initialPage);
  }, [initialPage]);

  // Calculate the current page data
  const currentPageData = sortedData.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage
  );

  // Calculate max pages
  const maxPages = Math.ceil(sortedData.length / itemsPerPage);

  // Handle sort request
  const handleSortRequest = useCallback(
    (key: string) => {
      if (
        sortDirection === SortDirection.Descending &&
        activeSortColumnKey === key
      ) {
        setActiveSortColumnKey('');
        setSortedData(data);
        return;
      }
      if (key === activeSortColumnKey) {
        setSortDirection((prev) =>
          prev === SortDirection.Ascending
            ? SortDirection.Descending
            : SortDirection.Ascending
        );
      } else {
        setActiveSortColumnKey(key);
        setSortDirection(SortDirection.Ascending); // Reset direction when new column is picked
      }
    },
    [activeSortColumnKey, data, sortDirection]
  );

  const handleRowClick = useCallback(
    (rowData: any) => {
      if (selectable) {
        const isRowSelected = selectedRows?.includes(rowData.id);

        if (isRowSelected) {
          setSelectedRows &&
            setSelectedRows((rows) => rows.filter((row) => row !== rowData.id));
        } else {
          setSelectedRows && setSelectedRows((rows) => [...rows, rowData.id]);
        }
      } else {
        onRowClick && onRowClick(rowData);
      }
    },
    [onRowClick, selectable, selectedRows, setSelectedRows]
  );

  const handleSelectRows = useCallback(() => {
    if (selectedRows && selectable && setSelectedRows) {
      const isAllRowsSelected = selectedRows.length === data.length;

      if (isAllRowsSelected) {
        setSelectedRows([]);
      } else {
        setSelectedRows(data.map((item) => item.id));
      }
    }
  }, [data, selectable, selectedRows, setSelectedRows]);

  if (!currentPageData.length) {
    return noDataRenderer ? noDataRenderer() : null;
  }

  return (
    <div className={[styles.table].join(' ')} style={style}>
      <Box
        sx={{
          overflowX: 'auto',
        }}
      >
        <TableHeader
          columns={columns}
          activeSortColumnKey={activeSortColumnKey}
          onSortClick={handleSortRequest}
          sortDirection={sortDirection}
          selectable={selectable}
          handleSelectRows={handleSelectRows}
          selectedRows={selectedRows}
          data={pagination ? currentPageData : data}
        />
        <TableRows
          columns={columns}
          data={pagination ? currentPageData : data}
          rowActions={rowActions!}
          roundLastRow={roundLastRow}
          rowHeight={rowHeight}
          register={register}
          tableKey={tableKey}
          selectedRows={selectedRows}
          onRowClick={handleRowClick}
          selectable={selectable}
        />
      </Box>
      {pagination && (
        <TableFooter
          currentPage={currentPage}
          maxPages={maxPages}
          setCurrentPage={setCurrentPage}
          pagination={pagination && maxPages > 1}
        />
      )}

      {additionalAction && additionalAction}
    </div>
  );
}

const Table = memo(TableComponent);

export default Table;
